import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const Pagination = ({ totalItems, currentPage, itemsPerPage, onPageChange, custombutton }) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
    const startItem = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
    const endItem = totalItems > 0 ? Math.min(currentPage * itemsPerPage, totalItems) : 0;

    const { t } = useTranslation(['pagination']);

    const [isSelectOpen, setIsSelectOpen] = useState(false);
    const selectRef = useRef(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 1024);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (selectRef.current && !selectRef.current.contains(event.target)) {
                setIsSelectOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleItemsPerPageChange = (newItemsPerPage) => {
        onPageChange(1, newItemsPerPage);
        setIsSelectOpen(false);
    };

    const renderPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = isMobile ? 3 : 5;

        const commonClasses = `min-w-[${isMobile ? '2rem' : '2rem'}] min-h-[${isMobile ? '2rem' : '2rem'}] flex items-center justify-center px-${isMobile ? '2' : '3'} py-1 mx-${isMobile ? '0.5' : '1'} rounded-xl transition-all duration-300 text-${isMobile ? 'xs' : 'sm'} font-semibold shadow-md hover:shadow-lg`;

        if (totalPages <= maxPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(
                    <button
                        key={i}
                        data-testid={`page-button-${i}`}
                        onClick={() => onPageChange(i, itemsPerPage)}
                        className={`${commonClasses} ${currentPage === i
                            ? 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg scale-105'
                            : 'bg-gradient-to-r from-gray-100 to-gray-200 hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white hover:scale-105 cursor-pointer'
                            }`}
                        disabled={custombutton === 'disable'}
                    >
                        {i}
                    </button>
                );
            }
        } else {
            // Always show page 1
            pages.push(
                <button
                    key={1}
                    data-testid="page-button-1"
                    onClick={() => onPageChange(1, itemsPerPage)}
                    className={`${commonClasses} ${currentPage === 1
                        ? 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg scale-105'
                        : 'bg-gradient-to-r from-gray-100 to-gray-200 hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white hover:scale-105 cursor-pointer'
                        }`}
                    disabled={custombutton === 'disable'}
                >
                    1
                </button>
            );

            // Show ellipsis if current page is far from page 1
            if (currentPage > (isMobile ? 2 : 3)) {
                pages.push(
                    <span
                        key="start-ellipsis"
                        className={`px-${isMobile ? '2' : '3'} min-w-[${isMobile ? '2rem' : '2.5rem'}] min-h-[${isMobile ? '2rem' : '2.5rem'}] flex items-center justify-center text-gray-500 text-${isMobile ? 'xs' : 'sm'} font-semibold`}
                    >
                        ...
                    </span>
                );
            }

            // Show pages around the current page
            const startPage = Math.max(2, currentPage - (isMobile ? 1 : 1));
            const endPage = Math.min(totalPages - 1, currentPage + (isMobile ? 1 : 1));

            for (let i = startPage; i <= endPage; i++) {
                pages.push(
                    <button
                        key={i}
                        data-testid={`page-button-${i}`}
                        onClick={() => onPageChange(i, itemsPerPage)}
                        className={`${commonClasses} ${currentPage === i
                            ? 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg scale-105'
                            : 'bg-gradient-to-r from-gray-100 to-gray-200 hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white hover:scale-105 cursor-pointer'
                            }`}
                        disabled={custombutton === 'disable'}
                    >
                        {i}
                    </button>
                );
            }

            // Show ellipsis if current page is far from the last page
            if (currentPage < totalPages - (isMobile ? 1 : 2)) {
                pages.push(
                    <span
                        key="end-ellipsis"
                        className={`px-${isMobile ? '2' : '3'} min-w-[${isMobile ? '2rem' : '2.5rem'}] min-h-[${isMobile ? '2rem' : '2.5rem'}] flex items-center justify-center text-gray-500 text-${isMobile ? 'xs' : 'sm'} font-semibold`}
                    >
                        ...
                    </span>
                );
            }

            // Always show the last page if there are multiple pages
            if (totalPages > 1) {
                pages.push(
                    <button
                        key={totalPages}
                        data-testid={`page-button-${totalPages}`}
                        onClick={() => onPageChange(totalPages, itemsPerPage)}
                        className={`${commonClasses} ${currentPage === totalPages
                            ? 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg scale-105'
                            : 'bg-gradient-to-r from-gray-100 to-gray-200 hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white hover:scale-105 cursor-pointer'
                            }`}
                        disabled={custombutton === 'disable'}
                    >
                        {totalPages}
                    </button>
                );
            }
        }

        return pages;
    };

    if (isMobile) {
        return (
            <div data-testid="pagination-mobile-container" className="flex flex-col items-center justify-between px-4 py-4 gap-4 w-full mt-2 bg-white rounded-2xl border border-gray-200 ">
                <div data-testid="mobile-items-per-page-container" className="flex items-center justify-between w-full relative" ref={selectRef}>
                    <span data-testid="mobile-items-per-page-label" className="mr-3 text-sm font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">{t('pagination.itemper', { ns: 'pagination' })}</span>
                    <div data-testid="mobile-items-per-page-select" className="relative w-24">
                        <button
                            data-testid="mobile-items-per-page-toggle"
                            className={`w-full px-4 py-2 rounded-xl border border-gray-300 bg-white text-sm text-left text-gray-800 flex justify-between items-center shadow-md hover:shadow-lg transition-all duration-300 ${custombutton === 'disable' ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'cursor-pointer hover:border-[#4ABDBD] focus:ring-2 focus:ring-[#4ABDBD]'}`}
                            onClick={() => custombutton !== 'disable' && setIsSelectOpen(!isSelectOpen)}
                            disabled={custombutton === 'disable'}
                        >
                            <span data-testid="mobile-items-per-page-value">{itemsPerPage}</span>
                            <svg
                                data-testid="mobile-items-per-page-arrow"
                                className={`w-4 h-4 ml-2 transform transition-transform duration-300 ${isSelectOpen ? 'rotate-180' : ''} ${custombutton === 'disable' ? 'text-gray-400' : 'text-[#4ABDBD]'}`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {isSelectOpen && custombutton !== 'disable' && (
                            <div data-testid="mobile-items-per-page-dropdown" className="absolute z-10 bottom-full mb-2 w-full bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
                                {[5, 10, 20].map((option) => (
                                    <button
                                        key={option}
                                        data-testid={`mobile-items-per-page-option-${option}`}
                                        className={`w-full px-4 py-2 cursor-pointer text-sm text-gray-800 hover:bg-gradient-to-r hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white text-left transition-all duration-300 ${itemsPerPage === option ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white' : ''}`}
                                        onClick={() => handleItemsPerPageChange(option)}
                                    >
                                        {option}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div data-testid="mobile-pagination-controls" className="flex items-center justify-center gap-2">
                    <button
                        data-testid="mobile-prev-page-button"
                        onClick={() => onPageChange(currentPage - 1, itemsPerPage)}
                        disabled={currentPage === 1 || custombutton === 'disable'}
                        className={`p-2 min-w-[2rem] min-h-[2rem] rounded-xl flex items-center justify-center transition-all duration-300 text-sm shadow-md hover:shadow-lg ${currentPage === 1 || custombutton === 'disable'
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-gray-200 to-gray-300 hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white hover:scale-105 cursor-pointer'
                            }`}
                    >
                        <ChevronLeft data-testid="mobile-prev-page-icon" className="w-4 h-4" />
                    </button>

                    {renderPageNumbers()}

                    <button
                        data-testid="mobile-next-page-button"
                        onClick={() => onPageChange(currentPage + 1, itemsPerPage)}
                        disabled={currentPage === totalPages || custombutton === 'disable'}
                        className={`p-2 min-w-[2rem] min-h-[2rem] rounded-xl flex justify-center items-center transition-all duration-300 text-sm shadow-md hover:shadow-lg ${currentPage === totalPages || custombutton === 'disable'
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-gray-200 to-gray-300 hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white hover:scale-105 cursor-pointer'
                            }`}
                    >
                        <ChevronRight data-testid="mobile-next-page-icon" className="w-4 h-4" />
                    </button>
                </div>

                <div data-testid="mobile-pagination-info" className="text-sm font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent text-center">
                    {totalItems > 0 ? `${startItem}-${endItem} ${t('pagination.of', { ns: 'pagination' })} ${totalItems}` : `0-0 ${t('pagination.of', { ns: 'pagination' })} 0`}
                </div>
            </div>
        );
    }

    return (
        <div data-testid="pagination-desktop-container" className="flex flex-row items-center justify-between px-6 py-4 mt-2 bg-white rounded-md border border-gray-200">
            <div data-testid="desktop-items-per-page-container" className="flex items-center relative" ref={selectRef}>
                <span data-testid="desktop-items-per-page-label" className="mr-3 text-sm font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">{t('pagination.itemper', { ns: 'pagination' })}</span>
                <div data-testid="desktop-items-per-page-select" className="relative w-28">
                    <button
                        data-testid="desktop-items-per-page-toggle"
                        className={`w-full px-4 py-2 rounded-xl border border-gray-300 bg-white text-sm text-left text-gray-800 flex justify-between items-center shadow-md hover:shadow-lg transition-all duration-300 ${custombutton === 'disable' ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'cursor-pointer hover:border-[#4ABDBD] focus:ring-2 focus:ring-[#4ABDBD]'}`}
                        onClick={() => custombutton !== 'disable' && setIsSelectOpen(!isSelectOpen)}
                        disabled={custombutton === 'disable'}
                    >
                        <span data-testid="desktop-items-per-page-value">{itemsPerPage}</span>
                        <svg
                            data-testid="desktop-items-per-page-arrow"
                            className={`w-4 h-4 ml-2 transform transition-transform duration-300 ${isSelectOpen ? 'rotate-180' : ''} ${custombutton === 'disable' ? 'text-gray-400' : 'text-[#4ABDBD]'}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    {isSelectOpen && custombutton !== 'disable' && (
                        <div data-testid="desktop-items-per-page-dropdown" className="absolute z-10 bottom-full mb-2 w-full bg-white rounded-xl overflow-y-auto shadow-xl border border-gray-200">
                            {[5, 10, 20, 50].map((option) => (
                                <button
                                    key={option}
                                    data-testid={`desktop-items-per-page-option-${option}`}
                                    className={`w-full px-4 py-2 cursor-pointer text-sm text-gray-800 hover:bg-gradient-to-r hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white text-left transition-all duration-300 ${itemsPerPage === option ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white' : ''}`}
                                    onClick={() => handleItemsPerPageChange(option)}
                                    disabled={custombutton === 'disable'}
                                >
                                    {option}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div data-testid="desktop-pagination-controls" className="flex items-center gap-2">
                <button
                    data-testid="desktop-prev-page-button"
                    onClick={() => onPageChange(currentPage - 1, itemsPerPage)}
                    disabled={currentPage === 1 || custombutton === 'disable'}
                    className={`p-2 min-w-[2rem] min-h-[2rem] rounded-xl flex items-center justify-center transition-all duration-300 text-sm shadow-md hover:shadow-lg ${currentPage === 1 || custombutton === 'disable'
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-gray-200 to-gray-300 hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white hover:scale-105 cursor-pointer'
                        }`}
                >
                    <ChevronLeft data-testid="desktop-prev-page-icon" className="w-4 h-4" />
                </button>

                {renderPageNumbers()}

                <button
                    data-testid="desktop-next-page-button"
                    onClick={() => onPageChange(currentPage + 1, itemsPerPage)}
                    disabled={currentPage === totalPages || custombutton === 'disable'}
                    className={`p-2 min-w-[2rem] min-h-[2rem] rounded-xl flex justify-center items-center transition-all duration-300 text-sm shadow-md hover:shadow-lg ${currentPage === totalPages || custombutton === 'disable'
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-gray-200 to-gray-300 hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white hover:scale-105 cursor-pointer'
                        }`}
                >
                    <ChevronRight data-testid="desktop-next-page-icon" className="w-4 h-4" />
                </button>
            </div>

            <div data-testid="desktop-pagination-info" className="text-sm font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                {totalItems > 0 ? `${startItem}-${endItem} ${t('pagination.of', { ns: 'pagination' })} ${totalItems}` : `0-0 ${t('pagination.of', { ns: 'pagination' })} 0`}
            </div>
        </div>
    );
};

export default Pagination;


// <!-- Mobile View -->
// <button id="btn-items-per-page-toggle" class="...">10<svg>...</svg></button>
// <button id="btn-items-per-page-5" class="...">5</button>
// <button id="btn-items-per-page-10" class="...">10</button>
// <button id="btn-items-per-page-20" class="...">20</button>
// <button id="btn-prev-page" class="..."><svg>...</svg></button>
// <button id="btn-page-2" class="...">2</button>
// <button id="btn-page-3" class="...">3</button>
// <button id="btn-page-4" class="...">4</button>
// <button id="btn-next-page" class="..."><svg>...</svg></button>

// <!-- Desktop View -->
// <button id="btn-items-per-page-toggle" class="...">10<svg>...</svg></button>
// <button id="btn-items-per-page-5" class="...">5</button>
// <button id="btn-items-per-page-10" class="...">10</button>
// <button id="btn-items-per-page-20" class="...">20</button>
// <button id="btn-items-per-page-50" class="...">50</button>
// <button id="btn-prev-page" class="..."><svg>...</svg></button>
// <button id="btn-page-1" class="...">1</button>
// <button id="btn-page-2" class="...">2</button>
// <button id="btn-page-3" class="...">3</button>
// <button id="btn-page-4" class="...">4</button>
// <span>...</span>
// <button id="btn-page-5" class="...">5</button>
// <button id="btn-next-page" class="..."><svg>...</svg></button>


{/* <button
  id="btn-prev-page"
  onClick={() => onPageChange(currentPage - 1, itemsPerPage)}
  disabled={currentPage === 1 || custombutton === 'disable'}
  aria-label="Previous page"
  className="..."
></button> */}